import { IMDSTooltipProps, MDSTooltipPlacement } from "@/mds/components/tooltip/tooltip.types";
import { StyledTooltip } from "@/mds/components/tooltip/tooltip.style";
import { MDSShortcutKeyCombo } from "@/mds/components/shortcut-key";
import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds/components/text";
import { TooltipTrigger } from "react-aria-components";
import { useFocusable } from "react-aria";
import { HTMLProps, useRef } from "react";

const DEFAULT_CLOSE_DELAY_MS = 0;
const DEFAULT_OFFSET_PX = 4;

export const MDSTooltip = ({ config, isDisabled, children }: IMDSTooltipProps) => {
  if (!config) return children;
  const {
    label,
    shortcutLabel,
    shortcutHotkey,
    placement = MDSTooltipPlacement.Top,
    offset = DEFAULT_OFFSET_PX,
    delaySeconds,
  } = config;
  const delayMs = delaySeconds ? delaySeconds * 1000 : 200; // Default to 200ms

  return (
    <TooltipTrigger delay={delayMs} closeDelay={DEFAULT_CLOSE_DELAY_MS} isDisabled={isDisabled}>
      {children}
      <StyledTooltip placement={placement} offset={offset}>
        <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.OnAccent}>
          {label}
        </MDSText>
        {shortcutHotkey && <MDSShortcutKeyCombo hotkey={shortcutHotkey} variant="Tooltip" />}
        {!shortcutHotkey && shortcutLabel && (
          <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Tertiary}>
            {shortcutLabel}
          </MDSText>
        )}
      </StyledTooltip>
    </TooltipTrigger>
  );
};

/**
 * For MDSTooltip to work on non-MDSButton or MDSIconButton components, we need to use this component to wrap the trigger.
 */
export const MDSTooltipTrigger = (props: HTMLProps<HTMLDivElement>) => {
  const ref = useRef<HTMLDivElement>(null);
  const focus = useFocusable(props, ref);
  return (
    <div ref={ref} tabIndex={0} {...focus.focusableProps} {...props}>
      {props.children}
    </div>
  );
};
